/* eslint-disable complexity */
import React, { useContext, useEffect, useRef, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
	ICartOrderInfo,
	IOfferContext,
	ITollReservationInfo,
	ITypeVignettesOrderCompletedFields,
	IVignetteContext,
	OfferContext,
	VignetteContext,
	getVignettesOrderCompletedCollection,
	getLabelValue,
	setRouteLangParam,
	getUrlBasePath,
} from '@adac-mp/shared';
import {
	Wrapper,
	PageLead,
	BestPriceIcon,
	OrderSummaryBox,
	Button,
	Typography,
	Richtext,
	withLocalization,
	ContentLoader,
	OrderSummaryAccordion,
} from '@adac-mp/components';
import { useQuery } from '@apollo/client';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { useFlowId } from '@adac-mp/components/src/hooks/useFlowId';
import { renderTeasers } from '../utils/renderTeasers';

type UrlVariants = 'updatepayment' | null;

const OrderConfirmationPage = () => {
	const navigate = useNavigate();
	const flowId = useFlowId();
	const { lang, labels, resetOffer } = useContext<IOfferContext>(OfferContext);
	const { contentfulLabels, updateContentfulLabels } = useContext<IVignetteContext>(VignetteContext);
	const [orderInfo, setOrderInfo] = useState<ITollReservationInfo | ICartOrderInfo | undefined>(undefined);
	const unmountingComponent = useRef(false);
	const { viewport } = useViewport();
	const [variant, setVariant] = useState<UrlVariants>(null);
	const isOneCartFlow = flowId === 0 && !variant;

	const { error, data, loading } = useQuery(getVignettesOrderCompletedCollection, {
		variables: { lang: lang || 'de' },
	});

	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const urlVariant = urlParams.get('type') as UrlVariants;
		setVariant(urlVariant);
	}, []);

	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderCompletedCollection.items[0]);
		}
	}, [data]);

	const currentLabels = contentfulLabels as ITypeVignettesOrderCompletedFields;
	const teaserItems = currentLabels?.teasers?.items || [];

	useEffect(() => {
		resetOffer();
		const currOrderInfo = JSON.parse(sessionStorage.getItem('orderInfo') || '{}');
		if (!currOrderInfo?.price) {
			window.location.replace('https://www.adac.de/fahrzeugwelt/maut-vignette');
		}
		setOrderInfo(currOrderInfo);
		sessionStorage.removeItem('customerHash');
		// sessionStorage.removeItem('customerToken');
		sessionStorage.removeItem('orderDetail');
		sessionStorage.removeItem('cartHash');
		sessionStorage.removeItem('cartToken');
		sessionStorage.removeItem('offerHash');
		sessionStorage.removeItem('orderData');
		return () => {
			if (unmountingComponent.current) {
				sessionStorage.clear();
			} else {
				unmountingComponent.current = true;
			}
		};
	}, []);

	const getTotalOfferPrice = () => {
		return orderInfo?.price ? `${orderInfo.price.amount}€` : '';
	};
	const hasFlexProduct = orderInfo && 'flexProduct' in orderInfo && orderInfo.flexProduct;

	const renderVignetteOrderConfirmation = (vignetteOrderInfo: ITollReservationInfo | ICartOrderInfo) => (
		<OrderSummaryBox
			variant="vignette"
			orderNumber={vignetteOrderInfo?.orderNo || ''}
			totalPrice={getTotalOfferPrice()}
			hidePrice={Boolean(
				hasFlexProduct &&
					'orderDetails' in vignetteOrderInfo &&
					vignetteOrderInfo?.orderDetails.every(({ moduleTypeId }) => moduleTypeId === 4),
			)}
			itemLabel={currentLabels?.orderNumber || 'Bestellnummer'}
			priceLabel={
				hasFlexProduct
					? getLabelValue('mp.article', labels) || 'Artikel'
					: currentLabels?.priceDetails?.totalPriceHeadline || 'Gesamtpreis'
			}
			additionalInfo={
				hasFlexProduct
					? {
							title: currentLabels?.priceDetails?.flexServiceHeadline || 'FLEX-Service',
							label: currentLabels?.priceDetails?.flexServiceLabel || 'FLEX Modell zzgl. Mautgebühren',
					  }
					: undefined
			}
		/>
	);

	const renderPaymentUpdatedConfirmationSummaryFlex = () => (
		<OrderSummaryBox
			serviceCatalogName={getLabelValue('mp.tollSectionFlex', labels) || 'Streckenmaut Flex'}
			orderNumber={orderInfo?.orderNo || ''}
			itemLabel={getLabelValue('mp.service', labels) || 'Service'}
		/>
	);

	const renderPaymentUpdatedConfirmationSummaryMautbox = () => (
		<OrderSummaryBox
			serviceCatalogName={getLabelValue('mp.camperMautbox', labels) || 'Camper Mautbox'}
			orderNumber={orderInfo?.orderNo || ''}
			itemLabel={getLabelValue('mp.service', labels) || 'Service'}
		/>
	);

	const renderContactText = () => (
		<div className="flex items-center flex-col px-5 mb-5 md:mx-auto">
			<Typography component="p" size="sm" className="text-gray-300">
				{currentLabels?.contactText && <Richtext document={currentLabels.contactText as any} />}
			</Typography>
		</div>
	);

	const renderUpdatedPaymentContactText = () => (
		<div className="flex items-center flex-col px-5 mb-5 md:mx-auto">
			<Typography component="p" size="sm" className="text-gray-300">
				{currentLabels?.paymentUpdatedContactText && (
					<Richtext document={currentLabels.paymentUpdatedContactText as any} />
				)}
			</Typography>
		</div>
	);

	const renderUpdatePaymentConfirmation = () => (
		<>
			<PageLead
				icon={<BestPriceIcon />}
				headline={currentLabels?.updatedPaymentHeadline || 'Ihre Zahlungsart wurde aktualisiert'}
				description={
					currentLabels?.paymentUpdatedText ||
					'Sie erhalten in Kürze eine Bestätigung auf die für die Bestellung verwendete E-Mail Adresse.'
				}
			/>
			{hasFlexProduct
				? renderPaymentUpdatedConfirmationSummaryFlex()
				: renderPaymentUpdatedConfirmationSummaryMautbox()}
			{renderUpdatedPaymentContactText()}
			<Button
				fullWidth
				data-cy="Button: home"
				className="md:max-w-[292px]"
				onClick={() => {
					const params = createSearchParams();
					params.set('orderHash', String(orderInfo?.orderHash));
					navigate(getUrlBasePath(`/order-management${setRouteLangParam(lang)}`, params));
				}}>
				{currentLabels?.BackToOverview || 'Zurück zur Bestellübersicht'}
			</Button>
		</>
	);

	const renderOrderConfirmation = () => (
		<>
			<PageLead
				narrow={isOneCartFlow}
				icon={<BestPriceIcon />}
				headline={currentLabels?.headline || 'Bereit für den nächsten Trip!'}
				description={
					currentLabels?.text ||
					'Sollten Sie keine Bestellbestätigung per E-Mail erhalten, wenden Sie sich bitte mit Ihrer Bestellnummer an: maut-service@adac.de'
				}
			/>
			{orderInfo && isOneCartFlow ? (
				<OrderSummaryAccordion
					orderInfo={orderInfo as ICartOrderInfo}
					hasMembership={sessionStorage.getItem('memberDiscount') !== null}
				/>
			) : (
				orderInfo && renderVignetteOrderConfirmation(orderInfo)
			)}
			{renderContactText()}
			<Button
				fullWidth
				data-cy="Button: home"
				href={currentLabels?.backLink || 'https://www.adac.de/fahrzeugwelt/maut-vignette'}
				className="md:max-w-[292px]">
				{currentLabels?.backLinkText || 'Zurück zu Maut & Vignetten'}
			</Button>
		</>
	);

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					<Wrapper
						className="bg-white"
						style={{
							paddingBottom: viewport === 'desktop' ? '64px' : '48px',
							paddingTop: viewport === 'desktop' ? '48px' : '24px',
						}}>
						<section className="w-full text-center md:self-center md:w-7/12 lg:w-6/12">
							{variant === 'updatepayment'
								? renderUpdatePaymentConfirmation()
								: renderOrderConfirmation()}
						</section>
					</Wrapper>
					{renderTeasers({ viewport, currentLabels, teaserItems })}
				</>
			)}
		</>
	);
};

export const OrderConfirmation = withLocalization(OrderConfirmationPage);
